import { InfoCircleOutlined } from '@ant-design/icons';
import { Col, Form, Row, Skeleton, Tooltip, Popover } from 'antd';
import React, { ReactElement } from 'react';
import { withRouter } from 'react-router-dom';
import { getDefaultStr, stringToArray, withFieldName } from '../../../../modules/crm/inPatient/service/crmUtils';
import { GUTTOR_SPACE, HIS_VALUE_MAX_LENGTH, KEYS_TO_DIV_HIS_VALUES, WRAP_IN_DIV_FOR } from '../../../constants/commonConstants';
import NumberFormatView from '../../NumberFormatView/NumberFormatView';
import './CRMFormInput.less';
import { INPATIENT_PATH } from '../../../constants/crmConstants';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { isEmpty } from 'lodash';

const FormItem = (props: any): ReactElement | null => {
  let pageType = INPATIENT_PATH.NEW_ADMISSION;
  let formVal: string | number;

  const {
    formConfig, children: FormElement, location: { pathname: url },
    valuePropName, isSwitch, switchToggleValue, extraRules, hideToogleValue,
  } = props;
  const {
    hidden, label, size, classes, required, rules, key, isLoading, form, smallFloatLabel, smallPlaceholder, placeholder,
    itemNoStyle, isBillDetail, addonBefore, defaultValue, labelClass, searchType, fieldInFocus,
    hideLabel, offset, isToggleBesideLabel, arrToStr, tooltip, rightFooterText, leftFooterText, isEditableInDetail, smallLabel, floatLabel,
    isShowSuggestedValue, showSuggestedValueInBillDetails, suggestedValue, disabled, HISPayerValue, floatLabelWithCss,
  } = formConfig;
  const { suggestedValuesMap } = useSelector((state: RootState): any => state.hospital);
  let allRules: any = rules;
  const isValidSuggestedValue = suggestedValuesMap?.[key] || suggestedValue || HISPayerValue;

  if (extraRules?.length) {
    allRules = [...allRules, ...extraRules];
  }

  if (labelClass) {
    classes.push(labelClass);
  }

  if (url?.indexOf(INPATIENT_PATH.EDIT) >= 0) {
    pageType = INPATIENT_PATH.EDIT;
  } else if (url?.indexOf(INPATIENT_PATH.DISCHARGE) >= 0) {
    pageType = INPATIENT_PATH.DISCHARGE;
  } else if (url?.indexOf(INPATIENT_PATH.RECONSIDER) >= 0) {
    pageType = INPATIENT_PATH.RECONSIDER;
  } else if (url?.indexOf(INPATIENT_PATH.DETAIL) >= 0) {
    pageType = INPATIENT_PATH.DETAIL;
  }
  const editPage = pageType === INPATIENT_PATH.EDIT || pageType === INPATIENT_PATH.DISCHARGE || pageType === INPATIENT_PATH.RECONSIDER;
/**
 * Show label with suggested value if:
 * - Component is not disabled AND not a billDetail value AND
 * - Either should show suggested value or in edit page AND
 * - Has a valid suggested value
 */
  const showSuggestedValueWithLabel = Boolean(!disabled && !suggestedValue &&
    ((isShowSuggestedValue || editPage) && isValidSuggestedValue));

  if (form) {
    formVal = form?.getFieldsValue([key])[key];
  }

  const isLoadingComponent = (Component: ReactElement): ReactElement => {
    return <React.Fragment>
      <div className={`${isLoading ? '' : 'display-none'}`}>
        <Skeleton active={true} paragraph={{ rows: 0 }}
          className="full-skeleton" />
      </div>
      <div className={`${isLoading ? 'display-none' : ''}`}>
        {Component}
      </div>
    </React.Fragment>;
  };

  const getModifiedFormElement = (): ReactElement => {
    if (smallPlaceholder && !floatLabel) {
      return (
        <div className="form-field-wrapper">
          {React.cloneElement(FormElement, {
            placeholder: undefined,
          })}
          <div className="placeholder-with-small" aria-hidden="true">
            <span className="placeholder">{placeholder} { }</span>
            <span className="small-placeholder">{smallPlaceholder}</span>
          </div>
        </div>
      );
    }

    return FormElement;
  };

  // function to split all the diagnosis values coming from HIS
  const formatDiagnosisValues = (value: string): { shown: string[]; remaining: number; remainingValues: string[] } => {
    let HISdiagnosisValues: string[] = [];

    if (!isEmpty(value) && typeof value === 'string') {
      HISdiagnosisValues = stringToArray(value, ',');
    } else if (Array.isArray(value)) {
      HISdiagnosisValues = value;
    }
    const [shown, remainingValues] = [HISdiagnosisValues.slice(0, 2), HISdiagnosisValues.slice(2)];
    return {
      shown,
      remaining: remainingValues.length,
      remainingValues,
    };
  };

  const SuggestedValue = (params: { HISValue: string; className?: string }): ReactElement => {
    const { HISValue, className } = params;
    return (
      <div className={className || 'crm-form-element-suggested-value'}>
        <span className="suggested-value-title">HIS Value: </span>
          <Popover
            overlayClassName="suggested-value-popover"
            content={
              <span className="suggested-value-popover-content">{HISValue}</span>
            }
          >
            <span className="suggested-value-text">{HISValue}</span>
          </Popover>
      </div>
    );
  };

  // function to render the HIS value based on the field
  const renderSuggestedValue = (value: string): ReactElement | null => {
    if (KEYS_TO_DIV_HIS_VALUES.includes(key) && value) {
      const { shown, remaining, remainingValues } = formatDiagnosisValues(value);
      return (
        <>
          <div className="crm-form-element-suggested-value new-div-val">
            <span className="suggested-value-title">HIS Value: </span>
          </div>
          {shown?.map((HISValueToSplit: string, index: number): ReactElement => (
            <div key={index} className="crm-form-element-suggested-value">
              {HISValueToSplit.length > HIS_VALUE_MAX_LENGTH ? (
                <Popover
                  overlayClassName="suggested-value-popover"
                  content={
                    <span className="suggested-value-popover-content">{HISValueToSplit}</span>
                  }>
                  <span className="suggested-value-text">
                    {`${HISValueToSplit?.slice(0, HIS_VALUE_MAX_LENGTH)}...`}
                  </span>
                </Popover>
              ) : (
                <span className="suggested-value-text">{HISValueToSplit}</span>
              )}
            </div>
          ))}
          {remaining > 0 && (
            <div className="crm-form-element-suggested-value">
              <Popover
                overlayClassName="suggested-value-popover"
                content={
                  <span className="suggested-value-popover-content">
                    {remainingValues?.map((val: string, idx: number): ReactElement => (
                      <div
                        className="sug-remaining-val-div"
                        key={idx}>{val}
                      </div>
                    ))}
                  </span>
                }
              >
                <span className="suggested-value-text">+{remaining}</span>
              </Popover>
            </div>
          )}
        </>
      );
    }

    return (
      <SuggestedValue
        HISValue={value}
        className={`crm-form-element-suggested-value ${WRAP_IN_DIV_FOR.includes(key) ? 'new-div-val' : ''}`}
      />
    );
  };

  const getFormItemComponent = (isShowToolTip: boolean): ReactElement => {
    const labelContent = (isShowToolTip && tooltip) ? (
    // Show label with tooltip if tooltip exists and tooltip is enabled
      <div>
        {label} {' '}
        <Tooltip title={tooltip}>
          <InfoCircleOutlined />
        </Tooltip>
      </div>
    ) : (showSuggestedValueWithLabel) ? (
      <div className={`${WRAP_IN_DIV_FOR.includes(key) ? 'suggested-value-div' : 'suggested-value-wrapper'}`}>
        <div className="label-content">
          {label} {' '}
          {WRAP_IN_DIV_FOR.includes(key) ? <div>{renderSuggestedValue(suggestedValue || suggestedValuesMap?.[key])}</div> : null}
        </div>
        <div className="suggested-value-container">
          {WRAP_IN_DIV_FOR.includes(key) ? null : renderSuggestedValue(suggestedValue || suggestedValuesMap?.[key])}
        </div>
      </div>
    ) : label;

    // Field display name to use in validation messages
    const fieldDisplayName = typeof label === 'string' ? label : key;
    // Enhance all rules with the field display name
    const enhancedRules = allRules?.map((rule: any): any =>
      withFieldName(rule, fieldDisplayName),
    ) || [];
    // the suggestedValue over the form item is added to show the HIS value only over the input fields in Bill details card
    return isLoadingComponent(
      <div>
        {(isValidSuggestedValue && showSuggestedValueInBillDetails &&
          (<SuggestedValue
            HISValue={ suggestedValue || suggestedValuesMap?.[key]}
            className={`bill-detail-suggested-value ${!isBillDetail ? 'BD-with-dropdown' : ''}`}
          />)
        )}
        <Form.Item
          noStyle={itemNoStyle}
          label={
            (isSwitch || hideLabel || smallLabel || floatLabel) ? '' : (
              <span className="label-container">
                {required && <span className="required-asterisk">*</span>}
                <span>{labelContent}</span>
              </span>
            )
          }
          className={`${classes.join(' ')} custom-form-item`}
          required={required}
          rules={enhancedRules}
          name={key}
          id={key}
          valuePropName={valuePropName}
          initialValue={defaultValue}
        >
          {getModifiedFormElement()}
        </Form.Item>
        {(isValidSuggestedValue && fieldInFocus &&
          <SuggestedValue
            HISValue={ key === 'payer' ? HISPayerValue : suggestedValuesMap?.[key]}
            className={`crm-form-element-suggested-value new-div-val`}
          />
        )}
        {rightFooterText && <span className="crm-form-element-right-footer gray-text">{rightFooterText}</span>}
        {leftFooterText && <span className="left-Footer gray-text">{leftFooterText}</span>}
      </div>,
    );
  };

  const getDetailComponent = (): ReactElement => {
    const DetailComponent = isBillDetail ? <React.Fragment>
      <Row gutter={GUTTOR_SPACE}>
        <Col md={Math.abs(size / 2)}>
          {
            !itemNoStyle ? <p className="label-title">{label}</p> : null
          }
        </Col>
        <Col md={Math.abs(size / 2)}>
          <div className={['label-value', ...classes].join(' ')}>
            {addonBefore === '₹ '
              ? <NumberFormatView amount={formVal} prefix={addonBefore} fixedDecimalScale={true} />
              : getDefaultStr(formVal, true, arrToStr)
            }
          </div>
        </Col>
      </Row>
    </React.Fragment> : <React.Fragment>
      {
        !itemNoStyle ? hideLabel ? <p className="label-gap" /> : <p className="label-title">{label}</p> : null
      }
      <div className={['label-value', ...classes].join(' ')}>{addonBefore === '₹ '
        ? <NumberFormatView amount={formVal} prefix={addonBefore} fixedDecimalScale={true} />
        : getDefaultStr(formVal, true, arrToStr)
      }</div>
    </React.Fragment>;
    return isLoadingComponent(DetailComponent);
  };

  if (hidden) {
    return null;
  }

  const formItemComponentModifier = (): ReactElement => {
    return isSwitch ? !hideToogleValue ?
      <Row gutter={GUTTOR_SPACE}>
        <Col md={18} style={{ paddingTop: 8 }}>
          <p className="label-title">{label}</p>
        </Col>
        <Col md={3}>
          {getFormItemComponent(false)}
        </Col>
        <Col md={3}>
          <div style={{ marginTop: 6 }}>{switchToggleValue ? 'Yes' : 'No'}</div>
        </Col>
      </Row> :
      <div className={isToggleBesideLabel ? 'alignInline l-m-r-5' : 'alignInline j-content'}>
        <p className="label-title">{label}</p>
        {getFormItemComponent(false)}
      </div> : smallLabel ? <div>
        <div className="benef-small-label">
          <span className="label-container">
            {required && <span className="required-asterisk">*</span>}
            <span className="label-title">{label}</span><span className="label-sm-title">{smallLabel}</span>
          </span>
        </div>
        {getFormItemComponent(false)}
      </div>
      : floatLabel ? <>
        <span className={`his-float-label ${searchType} ${fieldInFocus ? 'blue-float' : ''} ${floatLabelWithCss ? 'signature-float-label' : ''}`}>
          {label}
          {smallFloatLabel && <>
            {' '}
            <small className="float-small-label">{smallFloatLabel}
            </small></>}
        </span>
        {getFormItemComponent(false)}
      </>
        : getFormItemComponent(true);
  };

  return (
    <Col md={size} offset={offset}>
      {
        editPage || pageType === INPATIENT_PATH.NEW_ADMISSION || isEditableInDetail
          ? formItemComponentModifier()
          : getDetailComponent()
      }
    </Col>
  );
};

export default withRouter(FormItem);
